import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {useActionMenuTokenInputConvertAmountToOpposite} from '#/components/actionmenu/token-input/use-action-menu-token-input-convert-amount-to-opposite';
import {tokenConstants} from '#/data/token-constants';
import {useCoinDisplayPreferences, useCoinDisplayTypes} from '#/hooks/use-coin-display-preferences';
import {TokenType} from '#/repository/types';
import {stringToTokenAmount, tokenAmountToInputString} from '#/utils/token-string-utils';
import {useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useToggle} from 'react-use';

const useTokenKeySelection = (initialSelectedKey: TokenType) => {
  const [isStableSelected, toggleIsStableSelected] = useToggle(initialSelectedKey === TokenType.STABLE);
  const selected = isStableSelected ? TokenType.STABLE : TokenType.LAND;
  const unselected = selected === TokenType.STABLE ? TokenType.LAND : TokenType.STABLE;

  return {
    selected,
    unselected,
    toggle: toggleIsStableSelected,
  };
};
const useTokenConstants = () => {
  const {land} = useContext(ActionMenuContext);

  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  const stableConstants = {
    key: TokenType.STABLE,
    currencySymbol: displayPref.symbol,
    decimals: 2,
    display: (amount: number) =>
      t('utils.stableAmount', {
        amount: displayPref.displayAmount(amount),
      }),
  };

  const landConstants = {
    key: TokenType.LAND,
    currencySymbol: land.tokenSymbol,
    decimals: 6,
    display: (amount: number) =>
      t('utils.nativeAmount', {
        amount,
      }),
  };

  return {
    stable: stableConstants,
    land: landConstants,
  };
};

const initialValidationMessage = '';
const useTokenInput = (initialValue: string) => {
  const [input, setInput] = useState(initialValue);
  const [validationMessage, setValidationMessage] = useState(initialValidationMessage);

  const reset = () => {
    setInput(initialValue);
    setValidationMessage(initialValidationMessage);
  };

  const onChange = (value: string) => {
    if (value === '') {
      reset();
      return;
    }

    setInput(value);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {value, validationMessage} = event.target;

    if (value === '' && validationMessage === '') {
      reset();
      return;
    }

    setValidationMessage(validationMessage);
    setInput(value);
  };

  const onInputBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = event.target;

    if (value === '' && !!validationMessage) {
      reset();
      return;
    }
  };

  return {
    input,
    validationMessage,
    reset,
    onChange,
    onInputChange,
    onInputBlur,
  };
};

const useAdjustedInputTokenAmount = (tokenType: TokenType, amount: number) => {
  const displayPref = useCoinDisplayPreferences();
  const {coinDisplayTypes} = useCoinDisplayTypes();

  if (tokenType === TokenType.STABLE && coinDisplayTypes === tokenConstants.USD) {
    return displayPref.numericDisplayAmountToUsdc(amount);
  }

  return amount;
};

const useTokenAmount = (input: string, tokenType: TokenType) => {
  const rawAmount = stringToTokenAmount(input);
  const amount = useAdjustedInputTokenAmount(tokenType, rawAmount);

  return amount;
};

const useActionMenuTokenInputAmounts = (
  tokenKeySelection: ReturnType<typeof useTokenKeySelection>,
  amount: number,
  convertedAmount: number,
) => {
  if (tokenKeySelection.selected === TokenType.STABLE) {
    return {
      stableTokenAmount: amount,
      landTokenAmount: convertedAmount,
    };
  }

  return {
    landTokenAmount: amount,
    stableTokenAmount: convertedAmount,
  };
};

export const useGetOnSwapTokenAmount = () => {
  const displayPref = useCoinDisplayPreferences();
  const {coinDisplayTypes} = useCoinDisplayTypes();

  const getSwappedAmount = (selectedTokenKey: TokenType, amount: number) => {
    if (selectedTokenKey === TokenType.STABLE && coinDisplayTypes === tokenConstants.USD) {
      return displayPref.usdcToNumericDisplayAmount(amount);
    }

    return amount;
  };

  return getSwappedAmount;
};

export const useActionMenuTokenInputData = ({
  initialValue = 0,
  initialSelectedKey,
  transactionType,
}: {
  initialValue?: number;
  initialSelectedKey: TokenType;
  transactionType: Parameters<typeof useActionMenuTokenInputConvertAmountToOpposite>[0];
}) => {
  const tokenKeySelection = useTokenKeySelection(initialSelectedKey);

  const tokenConstants = useTokenConstants();
  const convertTokenAmountToOpposite =
    useActionMenuTokenInputConvertAmountToOpposite(transactionType)[tokenKeySelection.selected];

  const selectedTokenConstants = tokenConstants[tokenKeySelection.selected];
  const unselectedTokenConstants = tokenConstants[tokenKeySelection.unselected];

  const tokenInput = useTokenInput(tokenAmountToInputString(initialValue, selectedTokenConstants.decimals, false));
  const amount = useTokenAmount(tokenInput.input, tokenKeySelection.selected);
  const convertedAmount = convertTokenAmountToOpposite(amount);

  const tokenAmounts = useActionMenuTokenInputAmounts(tokenKeySelection, amount, convertedAmount);

  return {
    tokenKeySelection,
    selectedTokenConstants,
    unselectedTokenConstants,
    tokenInput,
    amount,
    convertedAmount,
    tokenAmounts,
  };
};
